import { useEffect, useState } from 'react';
import { OpenInNew } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';

import { useConnectedPrescribers } from '../hooks/useConnectedPrescribers';

interface ProxyPrescriberControlsProps {
  getHref: (onBehalfOfProviderId: string) => string;
  buttonLabel?: string;
}

/**
 * GDN-1119: a Prescribing Proxy must open DoseSpot on behalf of a connected
 * prescriber. There is no in-DoseSpot picker, so JotPsych selects here:
 *  - 0 connected prescribers → disabled button + explanatory message
 *  - exactly 1 → used automatically, no picker
 *  - >1 → a picker; the chosen prescriber flows into the SSO URL
 *
 * Selection is ephemeral (per session) — the confirmed mechanism needs no
 * stored proxy↔prescriber linkage.
 */
export function ProxyPrescriberControls({
  getHref,
  buttonLabel = 'Open DoseSpot',
}: ProxyPrescriberControlsProps) {
  const { prescribers, isLoading } = useConnectedPrescribers();
  const [selectedId, setSelectedId] = useState('');

  // Auto-select when exactly one prescriber is available.
  useEffect(() => {
    if (prescribers.length === 1) {
      setSelectedId(prescribers[0].userId);
    }
  }, [prescribers]);

  if (isLoading) {
    return (
      <Button variant="contained" fullWidth disabled startIcon={<CircularProgress size={16} />}>
        Loading prescribers…
      </Button>
    );
  }

  if (prescribers.length === 0) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          A prescriber in your organization must finish DoseSpot setup before you
          can prepare prescriptions on their behalf.
        </Alert>
        <Button variant="contained" fullWidth disabled startIcon={<OpenInNew />}>
          {buttonLabel}
        </Button>
      </Box>
    );
  }

  const isMulti = prescribers.length > 1;
  const canOpen = prescribers.some(p => p.userId === selectedId);

  return (
    <Box>
      {isMulti && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="proxy-prescriber-label">Prescribe on behalf of</InputLabel>
          <Select
            labelId="proxy-prescriber-label"
            label="Prescribe on behalf of"
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
          >
            {prescribers.map(p => (
              <MenuItem key={p.userId} value={p.userId}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Button
        variant="contained"
        fullWidth
        component={canOpen ? 'a' : 'button'}
        href={canOpen ? getHref(selectedId) : undefined}
        target="_blank"
        rel="noopener noreferrer"
        disabled={!canOpen}
        startIcon={<OpenInNew />}
        sx={{ py: 1.25, fontWeight: 600, borderRadius: 1.5, boxShadow: 2 }}
      >
        {buttonLabel}
      </Button>
    </Box>
  );
}
